import * as React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { DayPicker } from 'react-day-picker'

import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'

export type CalendarProps = React.ComponentProps<typeof DayPicker>

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-4 bg-popover text-popover-foreground', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-6 sm:space-x-8 sm:space-y-0',
        month: 'space-y-5',
        caption_label: 'hidden',
        caption_dropdowns: 'flex justify-center items-center gap-3 mb-4',
        nav: 'flex items-center',
        button_previous: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-8 w-8 bg-muted/50 p-0 hover:bg-primary/10 hover:text-primary absolute left-1 top-1 rounded-xl transition-all border border-transparent hover:border-primary/20'
        ),
        button_next: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-8 w-8 bg-muted/50 p-0 hover:bg-primary/10 hover:text-primary absolute right-1 top-1 rounded-xl transition-all border border-transparent hover:border-primary/20'
        ),
        month_grid: 'w-full border-collapse',
        weekdays: 'flex justify-between mb-2',
        weekday:
          'text-muted-foreground/50 rounded-md w-9 font-black text-[10px] uppercase tracking-widest text-center',
        week: 'flex w-full mt-1 justify-between',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-bold text-[13px] aria-selected:opacity-100 text-center flex items-center justify-center cursor-pointer transition-all rounded-lg'
        ),
        day_button: 'h-full w-full p-0 font-bold',
        range_end: 'day-range-end',
        selected:
          '!bg-primary !text-primary-foreground hover:!bg-primary/90 focus:!bg-primary/90 rounded-lg shadow-lg shadow-primary/20',
        today:
          'bg-muted text-primary rounded-lg font-black relative after:content-[""] after:absolute after:bottom-1.5 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:bg-primary after:rounded-full',
        outside:
          'day-outside text-muted-foreground opacity-30 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
        disabled: 'text-muted-foreground opacity-20',
        range_middle:
          'aria-selected:!bg-primary/10 aria-selected:!text-primary !rounded-none',
        dropdown:
          'rdp-dropdown bg-muted/50 border border-border/50 rounded-xl px-3 py-1.5 text-[11px] font-black uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer hover:bg-muted transition-all appearance-none mx-1.5',
        dropdown_month: 'relative inline-flex items-center group mr-1.5',
        dropdown_year: 'relative inline-flex items-center group ml-1.5',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          if (orientation === 'left') return <ChevronLeft className='h-4 w-4' />
          return <ChevronRight className='h-4 w-4' />
        },
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
